import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, TrendingUp, DollarSign, AlertCircle, ArrowLeft, 
  UserCheck, UserX, Clock, RefreshCw, Loader2, Plus, Trash2, Shield, X
} from 'lucide-react';

export default function AdminPanel({ onNavigate }) {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview | team
  
  // Team management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '' });
  const [addingMember, setAddingMember] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamSuccess, setTeamSuccess] = useState('');

  // Fetch admin overview stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/overview');
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching admin stats:', err);
    }
    setLoading(false);
  }, [api]);

  // Fetch team members
  const fetchTeam = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const res = await api.get('/admin/service-team');
      setTeamMembers(res.data.team || []);
    } catch (err) {
      console.error('Error fetching team:', err);
    }
    setLoadingTeam(false);
  }, [api]);

  useEffect(() => {
    // Check if user has admin role
    if (user && user.role !== 'admin') {
      onNavigate('dashboard');
      return;
    }
    fetchStats();
    fetchTeam();
  }, [user, onNavigate, fetchStats, fetchTeam]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStats();
    await fetchTeam();
    setRefreshing(false);
  };

  const handleAddMember = async () => {
    setAddingMember(true);
    setTeamError('');
    setTeamSuccess('');
    
    try {
      const res = await api.post('/admin/service-team', newMember);
      setTeamSuccess(res.data.message);
      setShowAddModal(false);
      setNewMember({ name: '', email: '', password: '' });
      fetchTeam();
    } catch (err) {
      setTeamError(err.response?.data?.detail || 'Failed to create team member');
    }
    setAddingMember(false);
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the service team?`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/service-team/${memberId}`);
      setTeamSuccess(`${memberName} has been removed from the team`);
      fetchTeam();
    } catch (err) {
      setTeamError(err.response?.data?.detail || 'Failed to delete team member');
    }
  };

  // If user doesn't have admin role, show nothing (will redirect)
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-slate-400 mb-4">Admin access required.</p>
          <button
            onClick={() => onNavigate('dashboard')}
            className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2 rounded-xl"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0F172A' }}>
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => onNavigate('landing')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Panel</h1>
              <p className="text-sm text-slate-500">Administrator-only dashboard</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => onNavigate('service')}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl transition-colors"
            >
              <Users className="w-4 h-4" />
              Service Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 pt-6">
        <div className="flex gap-2 bg-slate-800/50 rounded-xl p-1 w-fit">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'overview' 
                ? 'bg-violet-600 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'team' 
                ? 'bg-violet-600 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Service Team
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                        <Users className="w-6 h-6 text-violet-400" />
                      </div>
                      <span className="text-2xl font-bold text-white">{stats?.total_users || 0}</span>
                    </div>
                    <h3 className="text-slate-400 text-sm">Total Users</h3>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                        <UserCheck className="w-6 h-6 text-emerald-400" />
                      </div>
                      <span className="text-2xl font-bold text-emerald-400">{stats?.active_subscribers || 0}</span>
                    </div>
                    <h3 className="text-slate-400 text-sm">Active Subscribers</h3>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-400" />
                      </div>
                      <span className="text-2xl font-bold text-amber-400">{stats?.trial_users || 0}</span>
                    </div>
                    <h3 className="text-slate-400 text-sm">Trial Users</h3>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                        <UserX className="w-6 h-6 text-red-400" />
                      </div>
                      <span className="text-2xl font-bold text-red-400">{stats?.churned_users || 0}</span>
                    </div>
                    <h3 className="text-slate-400 text-sm">Churned Users</h3>
                  </motion.div>
                </div>

                {/* Quick Actions */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                  <h2 className="text-lg font-bold text-white mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => onNavigate('service')}
                      className="flex items-center gap-3 p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                        <Users className="w-5 h-5 text-violet-400" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-white">Service Dashboard</p>
                        <p className="text-xs text-slate-400">Manage clients and issues</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => window.open('https://dashboard.razorpay.com', '_blank')}
                      className="flex items-center gap-3 p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-white">Razorpay Dashboard</p>
                        <p className="text-xs text-slate-400">View payments & subscriptions</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => window.open('https://business.facebook.com', '_blank')}
                      className="flex items-center gap-3 p-4 bg-slate-700/50 hover:bg-slate-700 rounded-xl transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-white">Meta Business Suite</p>
                        <p className="text-xs text-slate-400">View ad performance</p>
                      </div>
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'team' && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-bold text-white">Service Team Management</h2>
                    <p className="text-sm text-slate-400">Add or remove service team members</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAddModal(true);
                      setTeamError('');
                      setTeamSuccess('');
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Member
                  </button>
                </div>

                {/* Success/Error Messages */}
                {teamSuccess && (
                  <div className="mb-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-3 rounded-xl text-sm">
                    {teamSuccess}
                  </div>
                )}
                {teamError && (
                  <div className="mb-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl text-sm">
                    {teamError}
                  </div>
                )}

                {/* Team Table */}
                {loadingTeam ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No team members found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Name</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Email</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Role</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-slate-400 uppercase">Created</th>
                          <th className="py-3 px-4"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamMembers.map(member => (
                          <tr key={member.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  member.role === 'admin' ? 'bg-violet-500/20' : 'bg-emerald-500/20'
                                }`}>
                                  <Shield className={`w-4 h-4 ${
                                    member.role === 'admin' ? 'text-violet-400' : 'text-emerald-400'
                                  }`} />
                                </div>
                                <span className="font-medium text-white">{member.name || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-slate-300">{member.email}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                member.role === 'admin' 
                                  ? 'bg-violet-500/20 text-violet-300' 
                                  : 'bg-emerald-500/20 text-emerald-300'
                              }`}>
                                {member.role === 'admin' ? 'Admin' : 'Service POC'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-400 text-sm">
                              {member.created_at ? new Date(member.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-3 px-4">
                              {member.role !== 'admin' && member.id !== user?.id && (
                                <button
                                  onClick={() => handleDeleteMember(member.id, member.name)}
                                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                                  title="Remove from team"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Admin Info */}
            <div className="mt-6 text-center text-slate-500 text-sm">
              <p>Logged in as <strong className="text-slate-300">{user?.name || user?.email}</strong> (Admin)</p>
            </div>
          </>
        )}
      </main>

      {/* Add Member Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white">Add Service Team Member</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    placeholder="Enter full name"
                    className="w-full bg-slate-900/50 border border-slate-600 focus:border-violet-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    placeholder="Enter email address"
                    className="w-full bg-slate-900/50 border border-slate-600 focus:border-violet-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
                  <input
                    type="password"
                    value={newMember.password}
                    onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                    placeholder="Create a password"
                    className="w-full bg-slate-900/50 border border-slate-600 focus:border-violet-500 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors"
                  />
                </div>

                {teamError && (
                  <p className="text-red-400 text-sm">{teamError}</p>
                )}

                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-sm text-slate-400">
                    <strong className="text-slate-300">Note:</strong> New members will be assigned the <span className="text-emerald-400">Service POC</span> role. They can access the Service Dashboard but not this Admin Panel.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-600 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMember}
                  disabled={!newMember.name || !newMember.email || !newMember.password || addingMember}
                  className="flex-1 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-4 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {addingMember && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create Member
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

